/*
 * Aloha Baby: carrusel de los más vendidos (sections/aloha-bestsellers.liquid). Portado de la maqueta.
 *
 * - Las flechas avanzan una tarjeta; la barra muestra cuánto del carrusel ya se vio.
 * - Con "reducir movimiento" el salto es inmediato, sin desplazamiento suave.
 * - En el editor de temas, al elegir un bloque el carrusel lo trae a la vista.
 * - Es un custom element: cuando el editor recarga la sección (shopify:section:load), el
 *   elemento viejo suelta sus listeners al desconectarse y el nuevo arranca solo.
 * - Con data-personalize="true", reordena las tarjetas según lo que miró la visitante
 *   (assets/aloha-personalize.js) y el titular pasa a data-personal-heading. Las
 *   recomendaciones de Shopify llegan después: solo se aplican si la sección aún no se ha visto,
 *   para que nada se mueva frente a la visitante. En el editor se ve siempre el orden del admin.
 */
if (!customElements.get('aloha-bestsellers')) {
  customElements.define(
    'aloha-bestsellers',
    class AlohaBestsellers extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.aloha-bestsellers__track');
        this.prevButton = this.querySelector('[data-direction="prev"]');
        this.nextButton = this.querySelector('[data-direction="next"]');
        this.bar = this.querySelector('.aloha-bestsellers__progress span');
        if (!this.track || !this.prevButton || !this.nextButton) return;

        this.update = this.update.bind(this);
        this.onPrev = () => this.go(-1);
        this.onNext = () => this.go(1);
        this.onBlockSelect = (event) => this.reveal(event.target);

        this.prevButton.addEventListener('click', this.onPrev);
        this.nextButton.addEventListener('click', this.onNext);
        this.track.addEventListener('scroll', this.update, { passive: true });
        this.addEventListener('shopify:block:select', this.onBlockSelect);
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.update);
          this.resizeObserver.observe(this.track);
        } else {
          window.addEventListener('resize', this.update);
        }
        this.personalize();
        this.update();
      }

      personalize() {
        const interest = window.AlohaInterest;
        if (this.dataset.personalize !== 'true' || !interest) return;
        if (window.Shopify && window.Shopify.designMode) return;

        this.seen = false;
        if ('IntersectionObserver' in window) {
          this.seenObserver = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              this.seen = true;
              this.seenObserver.disconnect();
            }
          });
          this.seenObserver.observe(this);
        }

        this.applyOrder(new Set());
        interest.recommendations().then((recommended) => {
          if (recommended.size && !this.seen && this.isConnected) this.applyOrder(recommended);
        });
      }

      applyOrder(recommended) {
        const cards = Array.from(this.track.children);
        const ordered = window.AlohaInterest.rank(cards, recommended);
        if (!ordered) return;
        ordered.forEach((card) => this.track.append(card));
        this.track.scrollLeft = 0;
        const heading = document.getElementById(this.getAttribute('aria-labelledby'));
        if (heading && heading.dataset.personalHeading) heading.textContent = heading.dataset.personalHeading;
        this.dataset.personalized = 'true';
        this.update();
      }

      disconnectedCallback() {
        if (this.seenObserver) this.seenObserver.disconnect();
        if (!this.track) return;
        this.prevButton.removeEventListener('click', this.onPrev);
        this.nextButton.removeEventListener('click', this.onNext);
        this.track.removeEventListener('scroll', this.update);
        this.removeEventListener('shopify:block:select', this.onBlockSelect);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        else window.removeEventListener('resize', this.update);
      }

      get behavior() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      }

      step() {
        const first = this.track.firstElementChild;
        if (!first) return this.track.clientWidth;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 20;
        return first.getBoundingClientRect().width + gap;
      }

      go(direction) {
        this.track.scrollBy({ left: direction * this.step(), behavior: this.behavior });
      }

      reveal(card) {
        if (!this.track.contains(card)) return;
        this.track.scrollTo({ left: card.offsetLeft - this.track.firstElementChild.offsetLeft, behavior: this.behavior });
      }

      update() {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const ratio = max <= 1 ? 1 : (this.track.scrollLeft + this.track.clientWidth) / this.track.scrollWidth;
        if (this.bar) this.bar.style.width = (ratio * 100).toFixed(1) + '%';
        this.prevButton.disabled = this.track.scrollLeft <= 2;
        this.nextButton.disabled = this.track.scrollLeft >= max - 2;
      }
    }
  );
}
